// ARCHIVO COMPARTIDO app/web — editar en ambos repos. Verificar con `npm run check:shared` (TemControlApp).
//
// Festivos de España.
//
// FUENTES POR TIPO DE FESTIVO (decisión de producto, igual en app y web):
//   - Nacionales y regionales (CCAA) → la FUENTE OFICIAL: tabla del BOE en
//     ./officialHolidays.js para los años que la tienen (sin llamadas de red).
//     Solo para años SIN tabla: OpenHolidays (proyecto EU, sin token) y, si
//     falla o devuelve vacío (problemas conocidos de TLS/red en RN, sobre todo
//     en Android), Nager.date como fallback automático.
//   - Locales (provincia/localidad)   → SOLO desde el backend (Galera), donde
//     el super los crea con prefijo "[Provincia · Localidad] descripción".
//   - De EMPRESA                      → backend (/api/companyHoliday). Se SUMAN
//     a los oficiales y SOLO aplican a trabajadores de esa empresa (mismo
//     companyId). Caché propia por empresa: nunca se mezclan con las cachés
//     globales (se colarían a trabajadores de otras empresas).
//
// Endpoints del backend:
//   GET /api/holiday/spain/{year}            → todos del año
//   GET /api/holiday/spain/acList            → mapeo id ↔ nombre de CCAA (1..19)
//   POST /api/holiday/spain                  → alta (sólo super)
//   DELETE /api/holiday/spain/{id}           → baja (sólo super)
//   GET /api/companyHoliday/{companyId}?years=2026,2027
//                                            → festivos de empresa
//
// Mantenemos la API pública (subdivisionForCommunity, subdivisionForProvince,
// fetchSpainHolidays, fetchHolidaysSetForWorker, invalidateHolidayCache)
// intacta para no tocar useHolidays. Nuevas: fetchCompanyHolidays,
// invalidateCompanyHolidayCache, isCompanyHoliday, groupHolidaysByDate,
// describeHolidays.
//
// REGLA DEL DOMINGO (RD 2001/1983) — aquí NO se traslada nada por nuestra
// cuenta:
//   - Art. 45.2/45.3: solo las fiestas NACIONALES del art. 45.1 que caen en
//     domingo pasan al lunes, y cada CCAA puede sustituir ese lunes por una
//     fiesta propia. Qué CCAA disfruta el lunes lo dice el BOE, que lo publica
//     como una fila más (2026: 2-nov y 7-dic, solo en algunas CCAA).
//   - Art. 46: los festivos LOCALES se publican en su fecha real → nunca se
//     desplazan (tampoco los de empresa).
//   Para años sin tabla oficial, los datos de OpenHolidays/Nager se usan tal
//   cual (sin traslado). Los campos originalDate/shiftedFromSunday se
//   mantienen por compatibilidad: originalDate === date y shiftedFromSunday
//   siempre false.

import { PROVINCES, communityForProvince } from './spain';
import { hasOfficialYear, officialHolidaysFor } from './officialHolidays';
import { listAutonomousCommunities, getAllHolidaysForYear, getCompanyHolidays } from '../services/api';

// CCAA → ISO subdivision (sigue usándose como cache key estable).
const AC_TO_SUBDIVISION = {
  'Andalucía': 'ES-AN',
  'Aragón': 'ES-AR',
  'Asturias': 'ES-AS',
  'Principado de Asturias': 'ES-AS',
  'Baleares': 'ES-IB',
  'Islas Baleares': 'ES-IB',
  'Canarias': 'ES-CN',
  'Cantabria': 'ES-CB',
  'Castilla y León': 'ES-CL',
  'Castilla-La Mancha': 'ES-CM',
  'Cataluña': 'ES-CT',
  'Comunidad Valenciana': 'ES-VC',
  'Extremadura': 'ES-EX',
  'Galicia': 'ES-GA',
  'La Rioja': 'ES-RI',
  'Madrid': 'ES-MD',
  'Comunidad de Madrid': 'ES-MD',
  'Murcia': 'ES-MC',
  'Región de Murcia': 'ES-MC',
  'Navarra': 'ES-NC',
  'Comunidad Foral de Navarra': 'ES-NC',
  'País Vasco': 'ES-PV',
  'Ceuta': 'ES-CE',
  'Melilla': 'ES-ML',
};

const normalize = (s = '') =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

export const subdivisionForCommunity = (name) => {
  if (!name) return null;
  if (AC_TO_SUBDIVISION[name]) return AC_TO_SUBDIVISION[name];
  const target = normalize(name);
  for (const [k, v] of Object.entries(AC_TO_SUBDIVISION)) {
    if (normalize(k) === target) return v;
  }
  return null;
};

export const subdivisionForProvince = (province) => {
  const community = communityForProvince(province);
  return subdivisionForCommunity(community);
};

const isoOf = (s) => {
  if (!s) return null;
  const str = String(s);
  // El backend devuelve fechas tipo "2026-01-06" o ISO completo.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{4}-\d{2}-\d{2}T/.test(str)) return str.slice(0, 10);
  return null;
};

const pickName = (nameArr) => {
  if (!Array.isArray(nameArr)) return 'Festivo';
  const es = nameArr.find((n) => n.language === 'ES');
  if (es?.text) return es.text;
  return nameArr[0]?.text || 'Festivo';
};

// Parser del prefijo "[Provincia · Localidad] descripción" que ponemos al
// crear festivos locales — el backend solo soporta nivel CCAA.
const parseDescription = (raw) => {
  const text = String(raw || '').trim();
  const m = text.match(/^\[([^\]]+)\]\s*(.*)$/);
  if (!m) return { description: text, province: null, locality: null };
  const inside = m[1];
  const rest = m[2];
  // "Provincia · Localidad" o "Provincia"
  const parts = inside.split(/[·•|/]/).map((p) => p.trim()).filter(Boolean);
  const [province = null, locality = null] = parts;
  return { description: rest || text, province, locality };
};

// ── Cache de la lista de CCAA (id ↔ nombre). Carga lazy. ───────────────────
let acListPromise = null;
const loadAcList = () => {
  if (!acListPromise) {
    acListPromise = listAutonomousCommunities()
      .then((arr) => Array.isArray(arr) ? arr : [])
      .catch(() => []);
  }
  return acListPromise;
};

// eslint-disable-next-line no-unused-vars
const communityIdForName = async (name) => {
  if (!name) return null;
  const list = await loadAcList();
  if (!list.length) return null;
  const target = normalize(name);
  const found = list.find((c) => normalize(c.name) === target);
  return found ? found.id : null;
};

// ── Caducidad de las cachés ────────────────────────────────────────────────
// Las sesiones de la app duran días (queda en segundo plano): sin TTL, un
// festivo local creado/borrado por el super no llegaba hasta reiniciar.
const HOLIDAY_CACHE_TTL_MS = 6 * 60 * 60 * 1000;
// Se incrementa en cada invalidación: una carga que ya estaba en vuelo al
// invalidar NO puede volver a meter su resultado (viejo) en la caché.
let cacheGeneration = 0;

// ── Cache de festivos del backend por año (todos, sin filtrar). ────────────
// Devuelve Promise<{ ok, list }>: `ok=false` si la llamada al backend falló
// (así fetchSpainHolidays sabe que le faltan los locales y no cachea). Un
// fallo NO se queda cacheado.
const yearCache = new Map(); // year → { at, promise }
const loadHolidaysForYear = (year) => {
  const hit = yearCache.get(year);
  if (hit && Date.now() - hit.at < HOLIDAY_CACHE_TTL_MS) return hit.promise;
  const entry = { at: Date.now(), promise: null };
  entry.promise = getAllHolidaysForYear(year)
    .then((arr) => ({ ok: true, list: Array.isArray(arr) ? arr : [] }))
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[holidays] festivos del backend fallaron:', err?.message || err, '-', year);
      if (yearCache.get(year) === entry) yearCache.delete(year);
      return { ok: false, list: [] };
    });
  yearCache.set(year, entry);
  return entry.promise;
};

// ── Festivos de EMPRESA ────────────────────────────────────────────────────
// Caché SEPARADA por `${companyId}:${year}`. Nunca van a yearCache ni a la
// caché por subdivisión: esas se comparten entre trabajadores de TODAS las
// empresas y un festivo de empresa se colaría a los de otra.
// Si el endpoint aún no existe (404, backend sin desplegar) o no tenemos
// permiso (403), lo tratamos como "sin festivos de empresa" y lo cacheamos
// poco tiempo (10 min) para no martillear. Los fallos TRANSITORIOS (5xx del
// backend o sin red) devuelven [] con un backoff corto (2 min): producción
// responde 500 mientras el backend no está desplegado y, sin backoff, cada
// carga de pantalla repetía la petición. Cualquier otro fallo devuelve []
// SIN cachear (se reintenta en la próxima llamada). Un fallo aquí nunca
// puede romper los festivos oficiales.
const COMPANY_HOLIDAY_MISSING_TTL_MS = 10 * 60 * 1000;
const COMPANY_HOLIDAY_ERROR_BACKOFF_MS = 2 * 60 * 1000;

// 5xx del backend, o sin red (ApiError con reason 'network' de services/api,
// o el TypeError crudo de fetch: "Network request failed" / "Failed to fetch").
const isTransientCompanyHolidayError = (err) => {
  const status = Number(err?.status);
  if (Number.isFinite(status) && status >= 500 && status <= 599) return true;
  if (err?.reason === 'network') return true;
  return err?.name === 'TypeError' && /network|failed to fetch|load failed/i.test(String(err?.message || ''));
};
// Generación propia: invalidateCompanyHolidayCache no debe tirar las cargas
// oficiales en vuelo (invalidateHolidayCache sí incrementa ambas).
let companyCacheGeneration = 0;
const companyCache = new Map(); // `${companyId}:${year}` → { at, ttl, value }
const companyPending = new Map();

export const COMPANY_HOLIDAY_LABEL = 'Festivo de empresa';

// companyId válido (número > 0) o null.
const companyIdOf = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const isCompanyHoliday = (holiday) => holiday?.scope === 'company';

// Fila del backend → objeto festivo con los mismos campos que el resto.
// SIN traslado domingo→lunes: la empresa fija la fecha exacta.
const normalizeCompanyHoliday = (row, companyId) => {
  const date = isoOf(row?.date ?? row?.Date);
  if (!date) return null;
  const description = String(row?.description ?? row?.Description ?? '').trim();
  return {
    id: row?.id ?? row?.Id ?? null,
    date,
    originalDate: date,
    shiftedFromSunday: false,
    name: description || COMPANY_HOLIDAY_LABEL,
    description: description || COMPANY_HOLIDAY_LABEL,
    scope: 'company',
    province: null,
    locality: null,
    companyId: companyIdOf(row?.companyId ?? row?.CompanyId) ?? companyId,
    source: 'company',
  };
};

// Devuelve Promise<[{ id, date, name, description, scope:'company', companyId, source:'company' }]>.
// Nunca rechaza.
export const fetchCompanyHolidays = (companyId, year) => {
  const cid = companyIdOf(companyId);
  const y = Number(year);
  if (!cid || !Number.isFinite(y) || y <= 0) return Promise.resolve([]);
  const key = `${cid}:${y}`;
  const hit = companyCache.get(key);
  if (hit && Date.now() - hit.at < hit.ttl) return Promise.resolve(hit.value);
  if (hit) companyCache.delete(key);
  if (companyPending.has(key)) return companyPending.get(key);

  const generation = companyCacheGeneration;
  let promise = null;
  promise = (async () => {
    let value = [];
    let ttl = null;
    try {
      const arr = await getCompanyHolidays(cid, y);
      value = (Array.isArray(arr) ? arr : [])
        .map((row) => normalizeCompanyHoliday(row, cid))
        // Defensivo: solo los de ESTA empresa y ESTE año (por si el backend
        // ignora el filtro de años).
        .filter((h) => h && h.companyId === cid && h.date.startsWith(`${y}-`));
      ttl = HOLIDAY_CACHE_TTL_MS;
    } catch (err) {
      const status = Number(err?.status);
      if (status === 404 || status === 403) {
        ttl = COMPANY_HOLIDAY_MISSING_TTL_MS;
      } else if (isTransientCompanyHolidayError(err)) {
        ttl = COMPANY_HOLIDAY_ERROR_BACKOFF_MS;
        // eslint-disable-next-line no-console
        console.warn('[holidays] festivos de empresa fallaron (reintento en 2 min):', err?.message || err, '-', key);
      } else {
        // eslint-disable-next-line no-console
        console.warn('[holidays] festivos de empresa fallaron:', err?.message || err, '-', key);
      }
      value = [];
    }
    if (ttl != null && generation === companyCacheGeneration) {
      companyCache.set(key, { at: Date.now(), ttl, value });
    }
    return value;
  })();
  companyPending.set(key, promise);
  // Limpieza FUERA del async: si getCompanyHolidays lanzase de forma síncrona,
  // un `finally` interno correría antes de asignar `promise` y dejaría la
  // entrada colgada. Solo borramos NUESTRA entrada (tras invalidar puede
  // haber otra nueva). `promise` nunca rechaza.
  promise.then(() => {
    if (companyPending.get(key) === promise) companyPending.delete(key);
  });
  return promise;
};

// Limpia la caché de festivos de empresa (de una empresa o de todas). Llamarlo
// tras importar/borrar festivos de empresa.
export const invalidateCompanyHolidayCache = (companyId = null) => {
  const cid = companyIdOf(companyId);
  companyCacheGeneration += 1;
  if (!cid) {
    companyCache.clear();
    companyPending.clear();
    return;
  }
  for (const map of [companyCache, companyPending]) {
    for (const key of Array.from(map.keys())) {
      if (key.startsWith(`${cid}:`)) map.delete(key);
    }
  }
};

// Limpia caches (tras crear/borrar festivos en la admin de super, o en un
// "recargar" explícito del usuario, p.ej. pull-to-refresh en la app).
// También limpia la de festivos de empresa (de ese año o de todos).
export const invalidateHolidayCache = (year = null) => {
  if (year == null) yearCache.clear();
  else yearCache.delete(year);
  // ojo: no tocamos acListPromise — la lista de CCAA es estable.
  cacheGeneration += 1;
  cache.clear();
  pending.clear();
  companyCacheGeneration += 1;
  if (year == null) {
    companyCache.clear();
  } else {
    for (const key of Array.from(companyCache.keys())) {
      if (key.endsWith(`:${Number(year)}`)) companyCache.delete(key);
    }
  }
  companyPending.clear();
};

// React Native no manda User-Agent por defecto y algunos balanceadores
// rechazan esas peticiones (403/406). En el navegador NO lo ponemos: es una
// cabecera no "CORS-safelisted" y forzaría un preflight innecesario.
const IS_REACT_NATIVE = typeof navigator !== 'undefined' && navigator.product === 'ReactNative';
const REQUEST_HEADERS = IS_REACT_NATIVE
  ? { Accept: 'application/json', 'User-Agent': 'TemControl/1.0 (React Native)' }
  : { Accept: 'application/json' };

// fetchWithTimeout: el `fetch` nativo de RN no respeta un timeout razonable
// y a veces se queda colgado indefinidamente con TLS lento en redes 4G/5G.
// Forzamos un AbortController con un tope generoso para que la cascada de
// fallbacks pueda continuar si una fuente no responde a tiempo.
const fetchWithTimeout = (url, opts = {}, timeoutMs = 12000) => {
  if (typeof AbortController === 'undefined') return fetch(url, opts);
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  return fetch(url, { ...opts, signal: ctrl.signal })
    .finally(() => clearTimeout(t));
};

// Fuente principal de nacionales/regionales: OpenHolidays.
// (Querystring construido a mano: el URLSearchParams de RN no implementa
// `set`/`toString` en todas las versiones.)
const fetchOpenHolidays = async (year, subdivisionCode) => {
  const params = `countryIsoCode=ES&languageIsoCode=ES&validFrom=${year}-01-01&validTo=${year}-12-31${
    subdivisionCode ? `&subdivisionCode=${subdivisionCode}` : ''
  }`;
  const url = `https://openholidaysapi.org/PublicHolidays?${params}`;
  try {
    const res = await fetchWithTimeout(url, { headers: REQUEST_HEADERS });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (Array.isArray(data) ? data : [])
      .map((h) => {
        // Fecha tal cual (sin traslado domingo→lunes).
        const date = isoOf(h.startDate);
        return {
          date,
          originalDate: date,
          shiftedFromSunday: false,
          name: pickName(h.name),
          scope: (h.regionalScope || '').toLowerCase() || (h.nationwide ? 'national' : 'regional'),
          province: null,
          locality: null,
          source: 'openholidays',
        };
      })
      .filter((h) => h.date);
  } catch (err) {
    // Logueamos el motivo real para diagnosticar fallos de TLS/red (antes el
    // catch los silenciaba y devolvía calendario vacío).
    // eslint-disable-next-line no-console
    console.warn('[holidays] OpenHolidays falló:', err?.message || err, '-', url);
    return [];
  }
};

// Fallback: Nager.date. Independiente de OpenHolidays. Devuelve festivos
// nacionales y los regionales marcados por `counties` (formato ES-XX).
// Filtramos al subdivisionCode del worker para devolver solo los que le
// aplican (nacionales + los de su CCAA).
const fetchNagerHolidays = async (year, subdivisionCode) => {
  const url = `https://date.nager.at/api/v3/PublicHolidays/${year}/ES`;
  try {
    const res = await fetchWithTimeout(url, { headers: REQUEST_HEADERS });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return (Array.isArray(data) ? data : [])
      .filter((h) => {
        if (h.global) return true; // nacional → vale para cualquier worker
        if (!subdivisionCode) return false; // sin CCAA del worker, fuera
        return Array.isArray(h.counties) && h.counties.includes(subdivisionCode);
      })
      .map((h) => {
        // Fecha tal cual (sin traslado domingo→lunes).
        const date = isoOf(h.date);
        return {
          date,
          originalDate: date,
          shiftedFromSunday: false,
          name: h.localName || h.name || 'Festivo',
          scope: h.global ? 'national' : 'regional',
          province: null,
          locality: null,
          source: 'nager',
        };
      })
      .filter((h) => h.date);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[holidays] Nager.date falló:', err?.message || err, '-', url);
    return [];
  }
};

// Nacionales/regionales de la tabla oficial del BOE (officialHolidays.js), sin
// red. Solo para años con hasOfficialYear(year). Sin subdivisión → solo los
// nacionales (marcados para todas las CCAA).
const fetchOfficialHolidays = (year, subdivisionCode) =>
  officialHolidaysFor(year, subdivisionCode).map((h) => ({
    date: h.date,
    originalDate: h.date,
    shiftedFromSunday: false,
    name: h.name,
    scope: h.scope, // 'national' si lo marcan todas las CCAA; si no, 'regional'
    province: null,
    locality: null,
    source: 'boe',
  }));

// Caché por (subdivisión, año). Contiene TODOS los locales del backend: el
// filtro por provincia/localidad del trabajador se aplica después, en
// fetchHolidaysSetForWorker (así la caché se comparte entre trabajadores).
const cache = new Map(); // key → { at, value }
const pending = new Map();

// Devuelve [{ date, name, scope, province, locality, source }]
//   scope: 'national' | 'regional' | 'local' (o lo que devuelva OpenHolidays)
//   province/locality: solo en locales, del prefijo "[Prov · Loc]".
//   source: 'boe' | 'openholidays' | 'nager' | 'galera'.
// Lista SIN filtrar por trabajador: puede traer varias entradas por fecha.
export const fetchSpainHolidays = async (year, subdivisionCode = null) => {
  if (!year) return [];
  const key = `${subdivisionCode || 'NAT'}:${year}`;
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < HOLIDAY_CACHE_TTL_MS) return hit.value;
  if (hit) cache.delete(key);
  if (pending.has(key)) return pending.get(key);

  const generation = cacheGeneration;
  let promise = null;
  promise = (async () => {
    try {
      // Nacionales/regionales (tabla oficial del BOE; sin tabla para ese año,
      // OpenHolidays → Nager) y locales del backend en paralelo.
      const [nationalRegional, backendYear] = await Promise.all([
        (async () => {
          // Año con tabla oficial → manda ella, sin llamadas de red.
          if (hasOfficialYear(year)) return fetchOfficialHolidays(year, subdivisionCode);
          const oh = await fetchOpenHolidays(year, subdivisionCode);
          if (oh.length > 0) return oh;
          // eslint-disable-next-line no-console
          console.info('[holidays] OpenHolidays vacío, intentando Nager.date…');
          return fetchNagerHolidays(year, subdivisionCode);
        })(),
        loadHolidaysForYear(year),
      ]);

      const localesBackend = backendYear.list
        .map((h) => {
          const ac = Number(h.autonomousCommunity ?? h.AutonomousCommunity ?? -1);
          const rawDesc = h.description ?? h.Description ?? 'Festivo';
          const parsed = parseDescription(rawDesc);
          // Fecha real del festivo local: nunca se traslada (RD 2001/1983 art. 46).
          const date = isoOf(h.date ?? h.Date);
          return {
            id: h.id ?? null,
            date,
            originalDate: date,
            shiftedFromSunday: false,
            name: parsed.description || 'Festivo',
            scope: 'local',
            province: parsed.province,
            locality: parsed.locality,
            communityId: ac,
            source: 'galera',
            _hasBracket: !!(parsed.province || parsed.locality),
          };
        })
        // Solo los que vienen con el bracket "[Prov · Loc]" se consideran
        // locales. Los del backend sin bracket eran nacionales/regionales
        // viejos: ya no los pintamos desde el backend.
        .filter((h) => h.date && h._hasBracket);

      // NUNCA colapsar por fecha aquí: esta lista se cachea por subdivisión y
      // se comparte entre trabajadores de TODAS las provincias; el filtro por
      // trabajador (holidayAppliesToWorker) va DESPUÉS. Antes un local
      // "[Sevilla · …]" el 6-ene machacaba a Reyes (nacional) y, al filtrarlo
      // para un trabajador de fuera de Sevilla, ese día desaparecía entero.
      // Varias entradas por fecha son válidas; solo quitamos duplicados
      // exactos (misma fecha + ámbito + nombre + provincia + localidad).
      const seen = new Set();
      const out = [];
      for (const h of [...nationalRegional, ...localesBackend]) {
        const dedupeKey = [h.date, h.scope, normalize(h.name), normalize(h.province), normalize(h.locality)].join('|');
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        out.push(h);
      }
      // Solo cacheamos un resultado COMPLETO: nacionales/regionales no vacíos
      // (tabla oficial del BOE — siempre completa —, o bien OpenHolidays o
      // Nager respondieron) Y la llamada al backend de locales
      // OK. Un resultado parcial (p.ej. solo los locales porque cayeron las
      // dos APIs públicas, o sin locales porque falló el backend) se devuelve
      // pero NO se cachea, para que la próxima llamada reintente — antes se
      // quedaba así toda la sesión. Tampoco si se invalidó mientras tanto.
      const complete = nationalRegional.length > 0 && backendYear.ok;
      if (complete && generation === cacheGeneration) {
        cache.set(key, { at: Date.now(), value: out });
      }
      return out;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[holidays] fetchSpainHolidays error:', err?.message || err);
      return [];
    } finally {
      // Solo borramos NUESTRA entrada (tras invalidar puede haber otra nueva).
      if (pending.get(key) === promise) pending.delete(key);
    }
  })();
  pending.set(key, promise);
  return promise;
};

// ¿Aplica un festivo al trabajador? Los festivos LOCALES (los que el admin
// crea con "[Provincia · Localidad]") solo aplican si coincide su PROVINCIA
// (y su localidad, si ambos la especifican). Los nacionales y autonómicos
// aplican a todos. Así "Feria de Sevilla" cuenta solo para los trabajadores
// de Sevilla. Si el trabajador no tiene provincia, no filtramos (mejor no
// dejarle sin festivos que quitarle uno que le corresponde).
// Los de EMPRESA solo aplican si el companyId coincide — nunca por el
// "sin provincia no filtramos" (sería colarle festivos de otra empresa).
// Localidad normalizada: "SEVILLA CAPITAL" ≡ "Sevilla" (en el backend hay
// festivos locales con " capital" y los trabajadores tienen el nombre a secas).
const normalizeLocality = (s) => normalize(s || '').replace(/\s+capital$/, '');

export const holidayAppliesToWorker = (holiday, worker) => {
  if (!holiday) return false;
  if (holiday.scope === 'company') {
    const hc = companyIdOf(holiday.companyId);
    return hc != null && hc === companyIdOf(worker?.companyId);
  }
  if (holiday.scope !== 'local') return true;       // nacional / autonómico → todos
  const wProv = normalize(worker?.province || '');
  if (!wProv) return true;                          // sin provincia del worker → no filtramos
  const hProv = normalize(holiday.province || '');
  if (!hProv) return true;                          // festivo local sin provincia declarada
  if (hProv !== wProv) return false;                // otra provincia → NO aplica
  const hLoc = normalizeLocality(holiday.locality);
  const wLoc = normalizeLocality(worker?.locality);
  return !hLoc || !wLoc || hLoc === wLoc;           // misma provincia; si hay localidad, que coincida
};

// `worker` debe traer { autonomousCommunity, province, locality, companyId } —
// sin province/locality el filtro de locales no puede aplicarse; sin
// companyId no hay festivos de empresa.
// `yearOrYears`: un año o un array de años. Con varios años se combinan
// set + list (necesario para la bolsa de horas, cuyo cómputo abarca desde
// el epoch 2026 hasta hoy: con solo el año en curso, los festivos de años
// anteriores contaban como faltas).
// Sin comunidad NI provincia → sin festivos OFICIALES (mismo criterio que
// tenían los hooks), pero SÍ los de su empresa.
// `set` = unión de fechas; `list` conserva TODAS las entradas que aplican al
// trabajador (puede haber varias por fecha: nacional + local, oficial + de
// empresa…). Primero se filtra por trabajador y DESPUÉS se construye el set.
export const fetchHolidaysSetForWorker = async (worker, yearOrYears) => {
  const hasLocation = !!(worker?.autonomousCommunity || worker?.province);
  const subdivisionCode = hasLocation
    ? (subdivisionForCommunity(worker?.autonomousCommunity) ||
       subdivisionForProvince(worker?.province) ||
       null)
    : null;
  const companyId = companyIdOf(worker?.companyId);
  const years = Array.from(new Set(
    (Array.isArray(yearOrYears) ? yearOrYears : [yearOrYears])
      .map(Number)
      .filter((y) => Number.isFinite(y) && y > 0)
  ));
  const [perYear, perYearCompany] = await Promise.all([
    hasLocation ? Promise.all(years.map((y) => fetchSpainHolidays(y, subdivisionCode))) : [],
    companyId ? Promise.all(years.map((y) => fetchCompanyHolidays(companyId, y))) : [],
  ]);
  const list = [];
  for (const all of [...perYear, ...perYearCompany]) {
    for (const h of all) if (holidayAppliesToWorker(h, worker)) list.push(h);
  }
  return { set: new Set(list.map((h) => h.date)), list, subdivisionCode };
};

// Prioridad para cuando hace falta UN solo festivo por día (color / etiqueta
// única): local > regional > nacional > empresa. El oficial manda; el de
// empresa solo si ese día no hay ninguno oficial.
export const holidayScopeRank = (scope) => {
  if (scope === 'local') return 3;
  if (scope === 'regional') return 2;
  if (scope === 'company') return 0;
  return 1;
};

// Map<ISO, festivo[]> con TODOS los festivos de cada día: primero los
// oficiales (más específico primero) y después los de empresa.
export const groupHolidaysByDate = (list) => {
  const map = new Map();
  for (const h of Array.isArray(list) ? list : []) {
    if (!h?.date) continue;
    if (!map.has(h.date)) map.set(h.date, []);
    map.get(h.date).push(h);
  }
  for (const arr of map.values()) {
    arr.sort((a, b) => holidayScopeRank(b.scope) - holidayScopeRank(a.scope));
  }
  return map;
};

// Texto para tooltips: "Feria de Abril · Patrón (Festivo de empresa)".
// Nombres repetidos (p.ej. el mismo festivo como nacional y regional) salen
// una sola vez.
export const describeHolidays = (entries) =>
  Array.from(new Set(
    (Array.isArray(entries) ? entries : [])
      .filter(Boolean)
      .map((h) => (isCompanyHoliday(h) ? `${h.name} (${COMPANY_HOLIDAY_LABEL})` : h.name))
      .filter(Boolean)
  )).join(' · ');

export { PROVINCES };
